<template>
  <div class="register-page min-h-screen flex pt-20">
    <div class="container mx-auto px-4 mt-8">
      <div class="flex justify-center">
        <div class="w-full max-w-md">
          <UiCard class="shadow-sm">
            <UiCardContent class="p-6">
              <div class="mb-6">
                <h2 class="text-xl font-semibold mb-1">Create an Account</h2>
                <p class="text-sm text-muted-foreground">Fill in your details below to register</p>
              </div>
              
              <div v-if="error" class="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-md">
                {{ error }}
              </div>
              
              <div v-if="showEmailVerificationMessage" class="mb-4 p-4 bg-blue-50 text-blue-800 border border-blue-200 rounded-md">
                <div class="flex items-center mb-2">
                  <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                  </svg>
                  <h3 class="font-semibold">Check your email</h3>
                </div>
                <p class="text-sm">
                  We've sent a verification link to <strong>{{ userData.email }}</strong>. 
                  Please check your email and click the link to verify your account before logging in.
                </p>
                <p class="text-xs mt-2 text-blue-600">
                  Didn't receive the email? Check your spam folder or 
                  <button @click="resendVerificationEmail" class="underline hover:no-underline" :disabled="resendLoading">
                    {{ resendLoading ? 'Sending...' : 'resend verification email' }}
                  </button>
                </p>
              </div>
              
              <form @submit.prevent="handleRegister" class="space-y-4" v-if="!showEmailVerificationMessage">
                <div class="space-y-2">
                  <UiLabel htmlFor="email" class="text-sm">Email</UiLabel>
                  <UiInput 
                    type="email" 
                    id="email" 
                    v-model="userData.email" 
                    required
                    autocomplete="email"
                    placeholder="me@example.com"
                  />
                </div>
                
                <div class="space-y-2">
                  <UiLabel htmlFor="firstName" class="text-sm">First Name</UiLabel>
                  <UiInput 
                    type="text" 
                    id="firstName" 
                    v-model="userData.firstName" 
                    required
                    autocomplete="given-name"
                    placeholder="John"
                  />
                </div>
                
                <div class="space-y-2">
                  <UiLabel htmlFor="lastName" class="text-sm">Last Name</UiLabel>
                  <UiInput 
                    type="text" 
                    id="lastName" 
                    v-model="userData.lastName" 
                    required
                    autocomplete="family-name"
                    placeholder="Doe"
                  />
                </div>
                
                <div class="space-y-2">
                  <UiLabel htmlFor="password" class="text-sm">Password</UiLabel>
                  <UiInput 
                    type="password" 
                    id="password" 
                    v-model="userData.password" 
                    required
                    autocomplete="new-password"
                  />
                </div>
                
                <div class="space-y-2">
                  <UiLabel htmlFor="confirmPassword" class="text-sm">Confirm Password</UiLabel>
                  <UiInput 
                    type="password" 
                    id="confirmPassword" 
                    v-model="confirmPassword" 
                    required
                    autocomplete="new-password"
                  />
                </div>
                
                <UiButton 
                  type="submit" 
                  class="w-full bg-black hover:bg-black/90" 
                  :disabled="loading"
                >
                  <svg v-if="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Register
                </UiButton>
                
                <div class="text-center mt-4">
                  <p class="text-sm text-muted-foreground">
                    Already have an account? 
                    <router-link to="/login" class="text-primary hover:underline">Sign in</router-link>
                  </p>
                </div>
              </form>
              
              <div v-if="showEmailVerificationMessage" class="text-center mt-6">
                <p class="text-sm text-muted-foreground">
                  Already verified your email? 
                  <router-link to="/login" class="text-primary hover:underline">Sign in here</router-link>
                </p>
              </div>
            </UiCardContent>
          </UiCard>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import UiCard from '@/components/ui/card.vue';
import UiCardContent from '@/components/ui/card-content.vue';
import UiButton from '@/components/ui/button.vue';
import UiInput from '@/components/ui/input.vue';
import UiLabel from '@/components/ui/label.vue';
import apiService from '@/services/api.service';

export default {
  name: 'RegisterView',
  components: {
    UiCard,
    UiCardContent,
    UiButton,
    UiInput,
    UiLabel
  },
  data() {
    return {
      userData: {
        email: '',
        firstName: '',
        lastName: '',
        password: ''
      },
      confirmPassword: '',
      loading: false,
      error: null,
      showEmailVerificationMessage: false,
      resendLoading: false
    };
  },
  methods: {
    ...mapActions(['register']),
    async handleRegister() {
      // Validate passwords match
      if (this.userData.password !== this.confirmPassword) {
        this.error = 'Passwords do not match';
        return;
      }
      
      this.loading = true;
      this.error = null;
      
      try {
        await this.register(this.userData);
        // Show email verification message instead of redirecting
        this.showEmailVerificationMessage = true;
      } catch (error) {
        console.error('Registration error:', error);
        this.error = error.response?.data?.message || 'Registration failed. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    async resendVerificationEmail() {
      this.resendLoading = true;
      this.error = null;
      
      try {
        await apiService.resendVerification(this.userData.email);
        
        // Could show a temporary success message here
        console.log('Verification email resent successfully');
      } catch (error) {
        console.error('Resend verification error:', error);
        
        // Extract error message from API response
        if (error.response && error.response.data && error.response.data.message) {
          this.error = error.response.data.message;
        } else {
          this.error = 'Failed to resend verification email. Please try again.';
        }
      } finally {
        this.resendLoading = false;
      }
    }
  }
};
</script>
